/**
 * Industry Pack Service
 *
 * API client for the platform-admin industry-pack registry endpoint
 * (`GET /platform-admin/industry-packs`). Returns the list of in-code packs
 * available for installation onto a tenant via the existing template-seeding
 * endpoint with `source: 'pack:<key>'`. Pack content lives in backend code,
 * not in any database, so this is a pure read-only listing endpoint.
 */

import apiClient from "./apiClient";
import { API_ENDPOINTS } from "../config/api";

/**
 * Item shape returned by `GET /platform-admin/industry-packs`. Mirrors the
 * backend `IndustryPackListItem` exposed by `industry-packs.controller.ts`.
 *
 * `contentCounts` always carries one entry per supported entity type — even
 * when the pack omits that type — so the UI can render a deterministic
 * picker (zeroes show as "tom for denne pakken" rather than disappearing).
 */
export interface IndustryPackListItem {
  key: string;
  version: number;
  displayName: string;
  descriptionNo: string;
  contentCounts: {
    checklistGroups: number;
    folderTemplates: number;
    activityTypes: number;
    consentBases: number;
  };
}

export const industryPackService = {
  /**
   * List every industry pack registered in the backend.
   */
  async getAvailablePacks(): Promise<IndustryPackListItem[]> {
    const response = await apiClient.get<IndustryPackListItem[]>(
      API_ENDPOINTS.INDUSTRY_PACKS
    );
    return response.data;
  },
};
